import React, { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@pgs-core/ui/components/dialog";
import { Button } from "@pgs-core/ui/components/button";
import type { PageSection } from "../lib/editor-content";
import { DEFAULT_SECTION_PADDING, sectionTextColor } from "../lib/editor-content";
import { backgroundPreviewStyle, columnAlignClasses, designTokenStyle } from "../lib/section-preview";
import { ColumnBlocksEditor, type ColumnBlocksEditorHandle } from "./column-blocks-editor";
import { useMediaPicker } from "./media-picker-dialog";
import "./section-canvas.css";
import type { ApiClient } from "../lib/api";

type BlockData = Record<string, any>;

/**
 * Modal editor for ONE column's content: a single Editor.js instance with
 * Cancel / Done in the header (no X close). The editing surface is wrapped in
 * the same .pgs-site scope as the canvas — tenant design tokens, the section's
 * background/padding and the derived text colour — so content is edited in the
 * site's own look. Section-level properties live in the Properties overlay.
 */
export function ColumnEditorDialog({
  section,
  columnIndex,
  api,
  designTokens,
  activeModules,
  palette,
  onSave,
  onCancel,
  eventsEnabled,
  bookingEnabled,
  menuEnabled,
}: {
  section: PageSection;
  columnIndex: number;
  api: ApiClient;
  /** The tenant's design_tokens JSON (or null) — styles the editing surface. */
  designTokens?: string | null;
  /** Active module slugs — module pages join the button block's link picker. */
  activeModules?: string[];
  /** Tenant palette for the button block's colour picker. */
  palette?: string[];
  onSave: (blocks: BlockData[]) => void;
  onCancel: () => void;
  /** Forwarded to the block editor: offer the events block when the module is active. */
  eventsEnabled?: boolean;
  bookingEnabled?: boolean;
  menuEnabled?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const editorRef = useRef<ColumnBlocksEditorHandle | null>(null);
  const { pickImage, pickerElement } = useMediaPicker(api);
  const blocks = section.columns[columnIndex]?.blocks ?? [];
  const multi = section.settings.columns > 1;

  // Section look for the editing surface: alignment, padding and the colour
  // layer only. Full-height (a viewport-tall modal helps nobody) and the
  // background image (unreadable to type over) are canvas/live-site concerns.
  const { settings } = section;
  let sectionClassName = "content-section page-section";
  if (settings.align !== "left") sectionClassName += ` page-section--align-${settings.align}`;
  if (settings.padding !== DEFAULT_SECTION_PADDING) sectionClassName += ` page-section--pad-${settings.padding}`;
  const textColor = sectionTextColor(section.settings.background);
  const sectionStyle: React.CSSProperties = {
    ...backgroundPreviewStyle({ color: settings.background.color, imageKey: "" }),
    ...(textColor
      ? ({ "--text": textColor, "--text-muted": textColor, color: textColor } as React.CSSProperties)
      : {}),
  };

  const finish = async () => {
    setBusy(true);
    try {
      const handle = editorRef.current;
      onSave(handle ? await handle.getBlocks() : blocks);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open: boolean) => { if (!open) onCancel(); }}>
      {/* aria-describedby: the dialog has no description text (Radix warns otherwise). */}
      <DialogContent
        hideClose
        className="max-h-[90vh] max-w-3xl overflow-y-auto"
        aria-describedby={undefined}
      >
        <div className="flex items-center justify-between gap-2">
          <DialogTitle>
            Edit content{multi ? ` — column ${columnIndex + 1}` : ""}
          </DialogTitle>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={() => { void finish(); }} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Done
            </Button>
          </div>
        </div>

        <div className="pgs-site overflow-hidden rounded-md border" style={designTokenStyle(designTokens)}>
          <section className={sectionClassName} style={sectionStyle}>
            {/* The column's own horizontal alignment applies while editing;
                vertical alignment only shows on the canvas/live grid. */}
            <div className={`container ${columnAlignClasses(section.columns[columnIndex] ?? { blocks: [] })}`}>
              <ColumnBlocksEditor
                ref={(handle) => { editorRef.current = handle; }}
                blocks={blocks}
                api={api}
                placeholder="Start writing…"
                pickImage={pickImage}
                eventsEnabled={eventsEnabled}
                bookingEnabled={bookingEnabled}
                menuEnabled={menuEnabled}
                activeModules={activeModules}
                palette={palette}
                holderClassName="[&_.codex-editor__redactor]:pb-0"
              />
            </div>
          </section>
        </div>

        {pickerElement}
      </DialogContent>
    </Dialog>
  );
}
